import { useState } from 'react';
import { feedPosts } from '@/mocks/gossip';
import SectionHead from './Section';
import { TonePill } from './Section';

const cats = ['全部', '实锤', '录音', '聊天', '截图'];

const catColor: Record<string, string> = {
  实锤: 'bg-flame-500 text-white',
  录音: 'bg-gold-500 text-ink-950',
  聊天: 'bg-ink-900 text-paper-50',
  截图: 'bg-flame-700 text-white',
};

export default function FeedSection() {
  const [cat, setCat] = useState('全部');
  const posts = cat === '全部' ? feedPosts : feedPosts.filter((p) => p.category === cat);

  return (
    <section id="latest" className="bg-paper-100 py-10 md:py-14 scroll-mt-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
        <SectionHead
          kicker="最新挖掘帖 · LIVE FEED"
          title="一口一口，新鲜出炉"
          desc="按发布时间倒序滚动，实锤、录音、聊天、截图分门别类。每条都标注证据条数与热度，方便你快速判断含金量。"
          link="#hammer"
        />

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {cats.map((c) => (
              <TonePill key={c} active={cat === c} tone="ink" onClick={() => setCat(c)}>
                {c}
              </TonePill>
            ))}
          </div>
          <span className="ml-auto hidden items-center gap-1.5 text-xs text-ink-500 sm:flex">
            <i className="ri-focus-3-line" />
            追着实时更新吃，别错过反转
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((p, i) => {
            const wide = p.pined === true;
            return (
              <article
                key={p.id}
                className={`group flex flex-col overflow-hidden rounded-xl border border-paper-200 bg-paper-50 transition-shadow ${
                  wide ? 'sm:col-span-2 lg:col-span-2' : ''
                }`}
              >
                <div className={`relative overflow-hidden ${wide ? 'aspect-[16/8]' : 'aspect-[16/10]'}`}>
                  <img
                    src={p.cover}
                    alt={p.title}
                    title={`${p.title} ${p.category}证据合集`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-transparent to-transparent" />
                  <div className="absolute left-3 top-3 flex items-center gap-2">
                    <span className={`rounded-md px-2.5 py-1 text-xs font-black ${catColor[p.category]}`}>
                      {p.category}
                    </span>
                    {p.pined ? (
                      <span className="flex items-center gap-1 rounded-md bg-ink-950/70 px-2 py-1 text-xs font-bold text-gold-300 backdrop-blur">
                        <i className="ri-pushpin-2-fill" />
                        置顶热议
                      </span>
                    ) : null}
                  </div>
                  <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-md bg-ink-950/60 px-2 py-1 text-[11px] font-bold text-paper-50 backdrop-blur">
                    <i className="ri-fire-fill text-flame-400" />
                    {p.hot}
                  </div>
                </div>

                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <div className="flex items-center gap-2 text-[11px] font-semibold text-ink-400">
                    <i className="ri-time-line" />
                    {p.time}
                    <span className="text-paper-300">·</span>
                    <span className="truncate">{p.source}</span>
                  </div>
                  <h3
                    className={`mt-2.5 font-display font-black leading-snug text-ink-950 ${
                      wide ? 'text-lg md:text-xl' : 'text-base md:text-lg'
                    }`}
                  >
                    <a href="#latest" className="cursor-pointer group-hover:text-flame-700 transition-colors">
                      {p.title}
                    </a>
                  </h3>
                  <p className={`mt-2 text-sm leading-6 text-ink-600 ${wide ? 'md:pr-6' : ''} line-clamp-2`}>
                    {p.lead}
                  </p>

                  <div className="mt-auto flex items-center justify-between border-t border-paper-100 pt-3.5">
                    <div className="flex items-center gap-4 text-xs font-semibold text-ink-500">
                      <span className="flex items-center gap-1.5">
                        <i className="ri-file-list-3-line text-base text-gold-600" />
                        {p.evCount} 条证据
                      </span>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-chat-3-line text-base text-flame-500" />
                        {p.comments.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-ink-400">
                      <button type="button" aria-label="收藏" className="grid h-8 w-8 cursor-pointer place-items-center rounded-full hover:bg-paper-100 hover:text-flame-600">
                        <i className="ri-bookmark-line text-base" />
                      </button>
                      <button type="button" aria-label="分享" className="grid h-8 w-8 cursor-pointer place-items-center rounded-full hover:bg-paper-100 hover:text-gold-600">
                        <i className="ri-share-forward-line text-base" />
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <a
            href="#latest"
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-ink-900 bg-ink-900 px-6 py-3 text-sm font-bold text-paper-50 transition-colors hover:bg-ink-950"
          >
            再翻五页，看看更早的锤
            <i className="ri-arrow-down-line" />
          </a>
        </div>
      </div>
    </section>
  );
}